import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import Modal from "../components/Modal";
import { FileText } from "lucide-react";

const isNameMatch = (name1, name2) => {
  if (!name1 || !name2) return false;
  const n1 = String(name1).toLowerCase().replace(/[^a-z0-9\s]/g, "").trim();
  const n2 = String(name2).toLowerCase().replace(/[^a-z0-9\s]/g, "").trim();
  if (n1 === n2) return true;

  const w1 = n1.split(/\s+/);
  const w2 = n2.split(/\s+/);
  return w2.every(w => w1.includes(w)) || w1.every(w => w2.includes(w));
};

const cleanStr = (str) => String(str || "").replace(/[^a-zA-Z0-9]/g, "").toLowerCase();

const parseIdnNumber = (val) => {
  if (!val) return 0;
  const cleanVal = String(val).replace(/\./g, "").replace(",", ".");
  return parseFloat(cleanVal) || 0;
};

const formatTampilanBersih = (text) => {
  if (!text) return "-";
  return String(text).replace(/\s*\([^)]*\)\s*$/, '').trim();
};

const formatDriveUrl = (url) => {
  if (!url) return "";
  if (url.startsWith("data:image")) return url;
  
  const match = url.match(/\/d\/([^/]+)/) || url.match(/id=([^&]+)/);
  if (match && match[1]) {
    return `https://drive.google.com/thumbnail?id=${match[1]}&sz=w800`;
  }
  return url;
};

const PrintReport = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const API = process.env.REACT_APP_API_BASE_URL || "";

  const parsedData = useMemo(() => {
    try {
      const local1 = localStorage.getItem("printData");
      const local2 = localStorage.getItem("printData_RSIA");
      const localData = local1 || local2;
      const sessionData = sessionStorage.getItem("activePrintData");

      let finalData = null;

      if (localData) {
        finalData = JSON.parse(localData);
      } else if (sessionData) {
        finalData = JSON.parse(sessionData);
      }

      if (finalData && !finalData.nama) finalData = null;

      return finalData || {};
    } catch (e) {
      return {};
    }
  }, []);

  useEffect(() => {
    const local1 = localStorage.getItem("printData");
    const local2 = localStorage.getItem("printData_RSIA");
    const localData = local1 || local2;

    if (localData) {
      sessionStorage.setItem("activePrintData", localData);
      localStorage.removeItem("printData");
      localStorage.removeItem("printData_RSIA");
    }
  }, []);

  const stateData = location.state || {};
  const rawItems = stateData.items || parsedData.items;
  const items = useMemo(() => Array.isArray(rawItems) ? rawItems : [], [rawItems]);
  
  const extractedNama = items.length > 0 ? items[0].nama : "";
  const nama = extractedNama || stateData.nama || parsedData.nama || "";
  
  const type = stateData.type || (items.length > 0 ? (items[0].indikator_kpi ? "kpi" : "tabungan") : null) || parsedData.type || "kpi";
  const currentUser = stateData.currentUser || parsedData.currentUser || "";

  const [localItems, setLocalItems] = useState([]);
  const [deletedItems, setDeletedItems] = useState([]);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isFetchingData, setIsFetchingData] = useState(true);

  const [modalOpen, setModalOpen] = useState(false);
  const [loadingModalOpen, setLoadingModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [modalMessage, setModalMessage] = useState("");
  const [modalType, setModalType] = useState("info");
  const [modalConfirmOnly, setModalConfirmOnly] = useState(true); 
  
  const [confirmPrintAction, setConfirmPrintAction] = useState(null);

  const [showSignModal, setShowSignModal] = useState(false);
  const [activeSignLevel, setActiveSignLevel] = useState(null);

  const hariIni = new Date();
  const namaBulan = [
    "Januari", "Februari", "Maret", "April", "Mei", "Juni",
    "Juli", "Agustus", "September", "Oktober", "November", "Desember",
  ];
  const defaultDate = `${hariIni.getDate()} ${namaBulan[hariIni.getMonth()]} ${hariIni.getFullYear()}`;

  const [tanggal, setTanggal] = useState(defaultDate);
  const [atasan1, setAtasan1] = useState(items[0]?.nama_atasan_1 || "");
  const [atasan2, setAtasan2] = useState(items[0]?.nama_atasan_2 || "");

  const [isLockedAtasan1, setIsLockedAtasan1] = useState(!!items[0]?.nama_atasan_1);
  const [isLockedAtasan2, setIsLockedAtasan2] = useState(!!items[0]?.nama_atasan_2);

  const [masterAtasanList, setMasterAtasanList] = useState([]);
  const [suggest1, setSuggest1] = useState(false);
  const [suggest2, setSuggest2] = useState(false);

  const [reportStatus, setReportStatus] = useState("DRAFT");
  const [ttdAtasan1, setTtdAtasan1] = useState("");
  const [ttdAtasan2, setTtdAtasan2] = useState("");

  const jabatan = items?.[0]?.unit || "-";
  const departement = items?.[0]?.divisi || "-";
  const userEmail = items?.[0]?.email || "";
  const userSignature = formatDriveUrl(items?.[0]?.tanda_tangan || "");
  
  const isOwner = isNameMatch(currentUser, nama);
  const isSuperAdminUser = cleanStr(currentUser).includes("admin") || cleanStr(currentUser).includes("direktur");
  const isReviewer = !isOwner && reportStatus === "PENDING";
  
  const isCurrentUserAtasan1 = isReviewer && !ttdAtasan1 && isNameMatch(currentUser, atasan1);
  const isCurrentUserAtasan2 = isReviewer && !!ttdAtasan1 && !ttdAtasan2 && isNameMatch(currentUser, atasan2);

  const showModal = useCallback((title, message, type = "error", onConfirm = null, isConfirmOnly = true) => {
    setModalTitle(title);
    setModalMessage(message);
    setModalType(type);
    setConfirmPrintAction(() => onConfirm);
    setModalConfirmOnly(isConfirmOnly);
    setModalOpen(true);
  }, []);

  useEffect(() => {
    if (!nama || items.length === 0) {
      setIsFetchingData(false);
      return;
    }

    if (location.state?.skipFetch) {
      setLocalItems(items);
      setIsFetchingData(false);
      return;
    }

    if (location.state?.autoAddRow) setIsEditMode(true);

    const fetchMasterAndMerge = async () => {
      try {
        setIsFetchingData(true);
        const endpointMaster = type === "kpi" ? "/api/indikator-data" : "/api/tabungan-data";
        const endpointUser = type === "kpi" ? "/api/kpi-by-user" : "/api/tabungan-by-user";
        
        const pMaster = axios.get(`${API}${endpointMaster}`);
        const pUser = userEmail ? axios.post(`${API}${endpointUser}`, { email: userEmail }) : null;
        
        const [resMaster, resUser] = await Promise.all([pMaster, pUser]);

        let activeItems = items;
        let fStatus = "DRAFT"; 
        let fTtd1 = ""; 
        let fTtd2 = "";
        
        // 🔥 PERBAIKAN WARNING: Diambil dari array items, BUKAN dari state langsung 🔥
        let fNamaA1 = items[0]?.nama_atasan_1 || "";
        let fNamaA2 = items[0]?.nama_atasan_2 || "";
        
        if (resUser && resUser.data && resUser.data.result === "success") {
          const freshData = resUser.data.data || [];
          const myFresh = freshData.filter(it => isNameMatch(it.nama, nama));
          if (myFresh.length > 0) {
            activeItems = myFresh;
            myFresh.forEach(it => {
                if (it.status === "PENDING" || it.status === "APPROVED") fStatus = it.status;
                if (it.ttd_atasan_1 && it.ttd_atasan_1.trim() !== "") fTtd1 = it.ttd_atasan_1;
                if (it.ttd_atasan_2 && it.ttd_atasan_2.trim() !== "") fTtd2 = it.ttd_atasan_2;
                if (it.nama_atasan_1) fNamaA1 = it.nama_atasan_1;
                if (it.nama_atasan_2) fNamaA2 = it.nama_atasan_2;
            });
            setReportStatus(fStatus);
            setTtdAtasan1(fTtd1);
            setTtdAtasan2(fTtd2);
            
            if (fNamaA1) { setAtasan1(fNamaA1); setIsLockedAtasan1(true); }
            if (fNamaA2) { setAtasan2(fNamaA2); setIsLockedAtasan2(true); }
          }
        } else {
          fStatus = items[0]?.status || "DRAFT";
          fTtd1 = items[0]?.ttd_atasan_1 || "";
          fTtd2 = items[0]?.ttd_atasan_2 || "";
          items.forEach(it => {
              if (it.status === "PENDING" || it.status === "APPROVED") fStatus = it.status;
              if (it.ttd_atasan_1 && it.ttd_atasan_1.trim() !== "") fTtd1 = it.ttd_atasan_1;
              if (it.ttd_atasan_2 && it.ttd_atasan_2.trim() !== "") fTtd2 = it.ttd_atasan_2;
          });
          setReportStatus(fStatus);
          setTtdAtasan1(fTtd1);
          setTtdAtasan2(fTtd2);
        }

        let combinedItems = activeItems;

        if (resMaster && resMaster.data && resMaster.data.result === "success") {
          const masterData = resMaster.data.message || [];
          
          const uniqueNames = Array.from(new Set(masterData.map(m => m.nama || m.NAMA || m.Nama).filter(Boolean)));
          setMasterAtasanList(uniqueNames);

          const userMasterData = masterData.filter((m) => isNameMatch(m.nama || m.NAMA || m.Nama, nama));

          const matchedIds = new Set();
          combinedItems = userMasterData.map((masterRow, index) => {
            let match;
            if (type === "kpi") {
              match = activeItems.find((sub) => cleanStr(sub.indikator_kpi || sub.indikator_asli) === cleanStr(masterRow.indikator_kpi) && cleanStr(sub.area_kinerja) === cleanStr(masterRow.area_kinerja));
            } else {
              match = activeItems.find((sub) => cleanStr(sub.kerja_tabungan_gaji) === cleanStr(masterRow.kerja_tabungan_gaji || masterRow["Kerja Tabungan Gaji"] || masterRow.Kerja_Tabungan_Gaji) && cleanStr(sub.parameter) === cleanStr(masterRow.parameter));
            }

            let mBobot = masterRow.bobot || masterRow.Bobot || 0;
            let finalBobot = parseFloat(String(mBobot).replace("%", "").replace(",", ".")) || 0;
            if (finalBobot > 0 && finalBobot <= 1 && !String(mBobot).includes("%")) {
              finalBobot = Math.round(finalBobot * 100);
            }

            let finalNilai = masterRow.nilai || masterRow.Nilai || 0;

            if (match) {
              matchedIds.add(match.id || match.kpi_id); 
              let buktiAwal = match.bukti_teks || match.bukti_nilai || match.sumber || match.bukti || "";
              if (String(buktiAwal).includes("http") || String(buktiAwal).includes("drive.google")) buktiAwal = "";

              let matchBobot = match.bobot ? parseFloat(String(match.bobot).replace("%", "").replace(",", ".")) : finalBobot;
              if (matchBobot > 0 && matchBobot <= 1 && !String(match.bobot || "").includes("%")) {
                matchBobot = Math.round(matchBobot * 100);
              }

              return {
                ...match,
                id: match.id || match.kpi_id,
                area_kinerja: masterRow.area_kinerja || match.area_kinerja,
                parameter: masterRow.parameter || match.parameter,
                sumber: masterRow.sumber || match.sumber,
                indikator_kpi: masterRow.indikator_kpi || match.indikator_kpi,
                indikator_asli: match.indikator_kpi || match.indikator_asli,
                kerja_tabungan_gaji: masterRow.kerja_tabungan_gaji || masterRow["Kerja Tabungan Gaji"] || masterRow.Kerja_Tabungan_Gaji || match.kerja_tabungan_gaji,
                hasil_berwujud: masterRow.hasil_berwujud || match.hasil_berwujud,
                konversi_uang: masterRow.konversi_uang || match.konversi_uang,
                target: (match.target !== undefined && match.target !== "") ? match.target : (masterRow.target || "100"),
                nilai: match.nilai || finalNilai,
                bobot: matchBobot,
                actual: match.actual || "",
                bukti_nilai: buktiAwal,
              };
            } else {
              return {
                id: `temp-master-${index}-${Date.now()}`,
                area_kinerja: masterRow.area_kinerja || "",
                parameter: masterRow.parameter || "",
                sumber: masterRow.sumber || "",
                indikator_kpi: masterRow.indikator_kpi || "",
                indikator_asli: masterRow.indikator_kpi || "",
                kerja_tabungan_gaji: masterRow.kerja_tabungan_gaji || masterRow["Kerja Tabungan Gaji"] || masterRow.Kerja_Tabungan_Gaji || "",
                hasil_berwujud: masterRow.hasil_berwujud || "",
                konversi_uang: masterRow.konversi_uang || "",
                target: masterRow.target || "100",
                nilai: finalNilai,
                bobot: finalBobot,
                actual: "",      
                bukti_nilai: "",
                email: userEmail,
              };
            }
          });

          const unmatchedSubmitted = activeItems.filter((sub) => !matchedIds.has(sub.id || sub.kpi_id));
          unmatchedSubmitted.forEach((sub) => {
            let bobotAwal = String(sub.bobot || "0").trim();
            let bVal = parseFloat(bobotAwal.replace("%", "").replace(",", ".")) || 0;
            if (bVal > 0 && bVal <= 1 && !bobotAwal.includes("%")) {
              bVal = Math.round(bVal * 100);
            }

            let buktiAwal = sub.bukti_teks || sub.bukti_nilai || sub.sumber || sub.bukti || "";
            if (String(buktiAwal).includes("http") || String(buktiAwal).includes("drive.google")) buktiAwal = "";

            combinedItems.push({
              ...sub, id: sub.id || sub.kpi_id, bobot: bVal, nilai: sub.nilai || 0,
              actual: sub.actual || "", bukti_nilai: buktiAwal,
            });
          });
        }

        const ownerTtdUrl = formatDriveUrl(activeItems[0]?.tanda_tangan || items[0]?.tanda_tangan || "");
        const ttd1Url = formatDriveUrl(fTtd1);
        const ttd2Url = formatDriveUrl(fTtd2);

        const urlsToPreload = [ownerTtdUrl, ttd1Url, ttd2Url].filter(url => url && url.trim() !== "");
        
        await Promise.all(urlsToPreload.map(url => {
          return new Promise(resolve => {
            const img = new Image();
            img.onload = resolve;
            img.onerror = resolve; 
            img.src = url;
          });
        }));

        setLocalItems(combinedItems);
        setIsFetchingData(false);

      } catch (error) { 
        console.error("Error:", error);
        setIsFetchingData(false);
      }
    };
    
    fetchMasterAndMerge();
  }, [API, type, nama, departement, userEmail, location.state?.autoAddRow, items, currentUser, location.state?.skipFetch]); 

  useEffect(() => {
    if (!isFetchingData && localItems.length > 0) {
      const hasInvalidTarget = localItems.some(item => {
        const rawTarget = String(item.target || "");
        const containsInvalidChars = /[^0-9.,%\s]/.test(rawTarget);
        let cleanTarget = rawTarget.replace(/%/g, "").replace(/\./g, "").replace(",", ".");
        
        return cleanTarget === "" || isNaN(parseFloat(cleanTarget)) || containsInvalidChars;
      });

      if (hasInvalidTarget && !isEditMode) {
        showModal(
          "Peringatan: Target Tidak Valid",
          "Terdapat kolom 'Target' yang mengandung huruf atau teks (misal: '1. Assasment'). Anda WAJIB mengubahnya menjadi HANYA ANGKA agar nilai akhir dapat dihitung secara sistematis.",
          "warning",
          () => {
            setIsEditMode(true); 
          },
          true 
        );
      }
    }
  }, [isFetchingData, localItems, isEditMode, showModal]);

  const executePrintLogic = useCallback(() => {
    const hasZeroOrEmpty = localItems.some(item => {
      const targetVal = parseIdnNumber(item.target);
      const actualVal = parseIdnNumber(item.actual);
      return targetVal === 0 || actualVal === 0 || item.actual === "" || item.target === "";
    });

    if (hasZeroOrEmpty) {
      showModal(
        "Peringatan Data Kosong", 
        "Terdapat data Aktual atau Target yang bernilai 0 atau belum diisi. Apakah Anda yakin ingin melanjutkan cetak?", 
        "warning",
        () => {
           setTimeout(() => window.print(), 300);
        },
        false 
      );
    } else {
      window.print();
    }
  }, [localItems, showModal]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && (e.key === "p" || e.key === "P")) {
        e.preventDefault();
        executePrintLogic();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [executePrintLogic]);

  const handleEditCell = (id, field, value) => {
    let finalValue = value;
    if (field === "target" || field === "actual" || field === "bobot") {
      finalValue = String(finalValue).replace(/\./g, ",").replace(/[^0-9,%]/g, "");
    }
    setLocalItems((prev) => prev.map((item) => (item.id === id ? { ...item, [field]: finalValue } : item)));
  };

  const handleDeleteRow = (id) => {
    const itemToDel = localItems.find((item) => item.id === id);
    if (itemToDel) {
      setDeletedItems((prev) => [
        ...prev, 
        { 
          id: id, 
          indikator_kpi: itemToDel.indikator_asli || itemToDel.indikator_kpi || "", 
          kerja_tabungan_gaji: itemToDel.indikator_asli || itemToDel.kerja_tabungan_gaji || "" 
        }
      ]);
    }
    setLocalItems((prev) => prev.filter((item) => item.id !== id));
  };

  const handleCancelEdit = () => {
    setIsEditMode(false); setDeletedItems([]); navigate(".", { replace: true, state: { nama, type, items, currentUser } });
  };

  const handleBack = () => {
    if (window.history.length > 2) {
      navigate(-1);
    } else {
      navigate("/homepage"); 
    }
  };

  const handleRequestApproval = async () => {
    try {
      setLoadingModalOpen(true);
      const idsToSubmit = localItems.filter(item => !String(item.id).startsWith("temp-")).map(item => item.id);
      
      let atasanCount = 0;
      if (atasan1 && atasan1.trim() !== "") atasanCount++;
      if (atasan2 && atasan2.trim() !== "") atasanCount++;
      if (atasanCount === 0) atasanCount = 2;

      const payload = { type, ids: idsToSubmit, atasanCount, atasan1: atasan1, atasan2: atasan2 };
      const res = await axios.post(`${API}/api/request-approval`, payload);
      setLoadingModalOpen(false);
      
      if (res.data.result === "success") {
        setReportStatus(atasanCount === 0 ? "APPROVED" : "PENDING");
        setIsLockedAtasan1(true);
        setIsLockedAtasan2(true);
        showModal("Berhasil", "Permintaan persetujuan telah dikirim ke Atasan.", "success", null, true);
      } else {
        showModal("Gagal", res.data.message, "error", null, true);
      }
    } catch (err) {
      setLoadingModalOpen(false);
      showModal("Error", "Gagal menghubungi server.", "error", null, true);
    }
  };

  const submitSignature = async () => {
    const canvasEl = document.querySelector(".popup-signature-canvas");
    if (!canvasEl) return;
    
    const isCanvasBlank = (canvas) => {
      const blank = document.createElement('canvas');
      blank.width = canvas.width; blank.height = canvas.height;
      return canvas.toDataURL() === blank.toDataURL();
    };

    if (isCanvasBlank(canvasEl)) {
      showModal("Peringatan", "Mohon isi tanda tangan Anda di kotak yang disediakan sebelum menyetujui.", "warning", null, true);
      return;
    }

    try {
      setLoadingModalOpen(true);
      const ttdBase64 = canvasEl.toDataURL("image/png");
      const idsToSubmit = localItems.filter(item => !String(item.id).startsWith("temp-")).map(item => item.id);
      
      let atasanCount = 0;
      if (atasan1 && atasan1.trim() !== "") atasanCount++;
      if (atasan2 && atasan2.trim() !== "") atasanCount++;
      if (atasanCount === 0) atasanCount = 2;

      const payload = { type, ids: idsToSubmit, ttdBase64, level_approval: activeSignLevel, atasanCount };
      const res = await axios.post(`${API}/api/submit-approval`, payload);
      
      setLoadingModalOpen(false);
      setShowSignModal(false);

      if (res.data.result === "success") {
        if (activeSignLevel === 1) {
          setTtdAtasan1(ttdBase64);
        }
        if (activeSignLevel === 2) {
          setTtdAtasan2(ttdBase64);
        }
        
        let newStatus = "APPROVED";
        if (atasanCount === 2 && activeSignLevel === 1) newStatus = "PENDING";
        setReportStatus(newStatus);

        showModal("Berhasil", "Laporan berhasil ditandatangani dan disetujui.", "success", null, true);
      } else {
        showModal("Gagal", res.data.message, "error", null, true);
      }
    } catch (err) {
      setLoadingModalOpen(false);
      showModal("Error", "Gagal menyimpan tanda tangan.", "error", null, true);
    }
  };

  const handleSaveToDB = async () => {
    const hasInvalidTarget = localItems.some(item => {
      const rawTarget = String(item.target || "");
      const containsInvalidChars = /[^0-9.,%\s]/.test(rawTarget);
      let cleanTarget = rawTarget.replace(/%/g, "").replace(/\./g, "").replace(",", ".");
      return cleanTarget === "" || isNaN(parseFloat(cleanTarget)) || containsInvalidChars;
    });

    if (hasInvalidTarget) {
      showModal("Peringatan", "Masih ada target yang mengandung huruf atau teks. Harap hapus semua huruf dan sisakan HANYA ANGKA sebelum menyimpan.", "error", null, true);
      return;
    }

    try {
      setIsSaving(true); setLoadingModalOpen(true);
      const itemsToSave = localItems.map(item => {
        const targetVal = parseIdnNumber(item.target);
        const actualVal = parseIdnNumber(item.actual);
        let calculatedNilai = 0;
        if (targetVal === 0 && actualVal === 0) calculatedNilai = 100;
        else if (targetVal !== 0) calculatedNilai = (actualVal / targetVal) * 100;

        return { ...item, nilai: Math.round(calculatedNilai) };
      });
      
      const payload = { type, nama, divisi: departement, unit: jabatan, email: userEmail, items: itemsToSave, deletedItems: deletedItems };
      const res = await axios.post(`${API}/api/update-print`, payload);
      
      setLoadingModalOpen(false);
      
      if (res.data.result === "success") {
        showModal("Berhasil", "Perubahan berhasil disimpan ke Database Server!", "success", () => {
          setIsEditMode(false); 
          setDeletedItems([]);
          setLocalItems(itemsToSave);

          const currentStored = JSON.parse(sessionStorage.getItem("activePrintData") || "{}");
          sessionStorage.setItem("activePrintData", JSON.stringify({ 
            ...currentStored, 
            nama: nama || currentStored.nama, 
            type: type || currentStored.type, 
            currentUser: currentUser || currentStored.currentUser, 
            items: itemsToSave 
          }));
          navigate(".", { replace: true, state: { ...stateData, nama, type, items: itemsToSave, currentUser, skipFetch: true } });
        }, true); 
      } else {
        showModal("Gagal", "Gagal menyimpan: " + res.data.message, "error", null, true);
      }
    } catch (err) {
      setLoadingModalOpen(false); showModal("Error Server", "Terjadi kesalahan: " + err.message, "error", null, true);
    } finally { setIsSaving(false); }
  };

  let groupedKpi = {};
  let totalBobot = 0;
  let totalNilaiAkhir = 0;

  if (type === "kpi") {
    localItems.forEach((item) => {
      const area = item.area_kinerja || "-";
      if (!groupedKpi[area]) groupedKpi[area] = [];
      groupedKpi[area].push(item);
    });
  }

  if (!nama || items.length === 0) {
    return (
      <div style={{ height: "100vh", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", fontFamily: "sans-serif", background: "#f8fafc" }}>
        <h2 style={{ color: "#ef4444", marginBottom: "10px" }}>Laporan Tidak Ditemukan</h2>
        <p style={{ color: "#64748b", marginBottom: "20px" }}>Data cetak kosong atau tidak ditemukan. Silakan kembali ke Beranda dan klik Cetak lagi.</p>
        <button onClick={() => navigate("/homepage")} style={{ background: "#2563eb", color: "white", padding: "10px 20px", border: "none", borderRadius: "8px", cursor: "pointer", fontWeight: "bold" }}>
          Kembali ke Beranda
        </button>
      </div>
    );
  }

  if (isFetchingData) {
    return (
      <div style={{ height: "100vh", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", background: "linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)", fontFamily: "'Inter', system-ui, sans-serif", padding: "20px" }}>
        <div style={{ background: "rgba(255, 255, 255, 0.95)", backdropFilter: "blur(16px)", padding: "48px 40px", borderRadius: "24px", boxShadow: "0 4px 6px -1px rgba(0,0,0,0.05), 0 25px 50px -12px rgba(0,0,0,0.15)", display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", maxWidth: "420px", width: "100%" }}>
          <div style={{ position: "relative", width: "90px", height: "90px", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: "28px" }}>
            <div style={{ position: "absolute", inset: 0, borderRadius: "50%", border: "3px solid transparent", borderTopColor: "#0ea5e9", borderBottomColor: "#10b981", animation: "spin 1.5s cubic-bezier(0.68, -0.55, 0.265, 1.55) infinite" }}></div>
            <style>
              {`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}
            </style>
            <FileText size={36} strokeWidth={1.5} style={{ color: "#0f172a" }} />
          </div>
          <h3 style={{ fontSize: "1.4rem", fontWeight: "800", color: "#0f172a", margin: "0 0 12px 0" }}>Menyiapkan Laporan</h3>
          <p style={{ fontSize: "0.95rem", color: "#64748b", margin: 0 }}>Sedang memuat data dan mensinkronisasi gambar tanda tangan. Mohon tunggu...</p>
        </div>
      </div>
    );
  }

  const bulanIni = new Date().toLocaleDateString("id-ID", { month: "long", year: "numeric" }).toUpperCase();

  return (
    <div className="print-container">
      <style>{`
        .print-container, .print-container * { box-sizing: border-box; }
        .print-container { font-family: Arial, sans-serif; font-size: 11px; color: #000; background: #e5e7eb; margin: 0; padding: 20px; min-height: 100vh; }
        .print-container .print-wrapper { max-width: 297mm; margin: 0 auto; background: white; padding: 20px; box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1); position: relative; }

        .print-container .header-table { width: 100%; border-collapse: collapse; table-layout: fixed; margin-bottom: 20px; }
        .print-container .header-table td { border: 1px solid #000; padding: 8px; vertical-align: middle; word-wrap: break-word; font-size: 12px; }

        .print-container .main-table { width: 100%; border-collapse: collapse; table-layout: fixed; margin-bottom: 20px; }
        .print-container .main-table th, .print-container .main-table td { border: 1px solid #000; padding: 6px; vertical-align: middle; word-wrap: break-word; overflow-wrap: break-word; }
        .print-container .main-table th { background-color: #8faadc !important; text-align: center; font-weight: bold; font-size: 11px; text-transform: uppercase; }

        .print-container .action-buttons { display: flex; gap: 12px; margin-bottom: 20px; justify-content: space-between; align-items: center; }
        .print-container .left-btns, .print-container .right-btns { display: flex; gap: 10px; align-items: center; }
        .print-container .btn { display: inline-flex; align-items: center; gap: 5px; padding: 8px 16px; border-radius: 6px; cursor: pointer; font-weight: bold; border: none; font-size: 12px; transition: 0.2s; white-space: nowrap; }

        .print-container .btn-back { background-color: #f3f4f6; color: #4b5563; border: 1px solid #d1d5db; }
        .print-container .btn-print { background-color: #166534; color: white; }
        .print-container .btn-edit { background-color: #eab308; color: #fff; }
        .print-container .btn-save { background-color: #3b82f6; color: white; }
        .print-container .btn-cancel { background-color: #ef4444; color: white; }
        .print-container .btn-request { background-color: #2563eb; color: white; }
        .print-container .btn-approve { background-color: #10b981; color: white; }

        .print-container .btn-del { background-color: #ef4444; color: white; padding: 4px 8px; border-radius: 4px; cursor: pointer; border: none; font-size: 10px; }

        .print-container .status-badge { padding: 6px 12px; border-radius: 20px; font-weight: bold; font-size: 11px; display: inline-block; }
        .print-container .status-draft { background-color: #f3f4f6; color: #4b5563; border: 1px solid #d1d5db; }
        .print-container .status-pending { background-color: #fef08a; color: #854d0e; border: 1px solid #fde047; }
        .print-container .status-approved { background-color: #d1fae5; color: #065f46; border: 1px solid #10b981; }

        .print-container .edit-banner { background-color: #fef08a; color: #854d0e; padding: 10px; text-align: center; font-weight: bold; border-radius: 6px; margin-bottom: 15px; border: 1px solid #fde047; }
        .print-container .edit-input { width: 100%; box-sizing: border-box; border: 1px dashed #9ca3af; padding: 6px; font-family: inherit; font-size: inherit; background: #fdfde8; outline: none; resize: vertical; line-height: 1.4; }
        .print-container .edit-input:focus { border: 1px solid #eab308; background: #fff; }
        .print-container .input-locked { background: #f3f4f6 !important; border: 1px solid #e5e7eb !important; color: #9ca3af; cursor: not-allowed; }

        .print-container .signature-section { display: flex; justify-content: space-between; align-items: flex-end; margin-top: 40px; page-break-inside: avoid; gap: 20px; }
        .print-container .sign-box { width: 30%; text-align: center; font-size: 12px; }
        .print-container .sign-space { height: 80px; display: flex; flex-direction: column; justify-content: center; align-items: center; margin: 5px 0; }
        .print-container .sign-line { border-top: 1px solid #000; width: 100%; padding-top: 8px; margin-top: 5px; position: relative; }

        @media screen { .print-container .only-print { display: none !important; } }

        @media print {
          @page { size: landscape; margin: 15mm 10mm 15mm 10mm; }
          body { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; background-color: white; padding: 0; }
          .print-container { background-color: white; padding: 0; }
          .print-container table { page-break-inside: auto; }
          .print-container tr { page-break-inside: avoid; page-break-after: auto; }
          .print-container thead { display: table-header-group; }
          .print-container .print-wrapper { max-width: 100%; margin: 0 auto; padding: 0 !important; box-shadow: none !important; }
          .print-container .no-print { display: none !important; }
          .print-container .hide-on-print { display: none !important; }
        }
      `}</style>

      <div className="print-wrapper">
        <div className="action-buttons no-print">
          <div className="left-btns">
            {!isEditMode && (<button className="btn btn-back" onClick={handleBack} disabled={isSaving}>← Kembali</button>)}
            {isEditMode ? (
              <>
                <button className="btn btn-save" onClick={handleSaveToDB} disabled={isSaving}>💾 Simpan</button>
                <button className="btn btn-cancel" onClick={handleCancelEdit} disabled={isSaving}>❌ Batal</button>
              </>
            ) : (
              <>
                {(reportStatus === "DRAFT" || !reportStatus) && <button className="btn btn-edit" onClick={() => setIsEditMode(true)}>✏️ Edit Data</button>}
                <div className={`status-badge status-${(reportStatus || "draft").toLowerCase()}`}>
                  STATUS: {reportStatus || "DRAFT"}
                </div>
              </>
            )}
          </div>
          <div className="right-btns">
            {!isEditMode && (
              <>
                {(!ttdAtasan1 || !ttdAtasan2) && (isOwner || isSuperAdminUser) && (
                  <button className="btn btn-request" onClick={handleRequestApproval}>
                     {reportStatus === "PENDING" ? "Update Permintaan TTD" : "Kirim Permintaan TTD"}
                  </button>
                )}
                {reportStatus === "PENDING" && !isCurrentUserAtasan1 && !isCurrentUserAtasan2 && (
                  <span style={{ fontStyle: "italic", color: "#6b7280", fontWeight: "bold" }}>⏳ Menunggu Atasan...</span>
                )}
                {reportStatus === "APPROVED" && (
                  <button className="btn btn-print" onClick={executePrintLogic}>🖨️ Cetak Dokumen</button>
                )}
              </>
            )}
          </div>
        </div>

        {isEditMode && (
          <div className="edit-banner no-print">
            Mode Edit Aktif! Perubahan akan langsung disimpan ke Server.
            <br />
            (Kolom <b>Aktual</b>, <b>Nilai</b>, dan <b>Nilai Akhir</b> terkunci untuk menjaga keaslian data).
          </div>
        )}

        <table className="header-table">
          <tbody>
            <tr>
              <td style={{ width: "15%", fontWeight: "bold" }}>NAMA</td>
              <td style={{ width: "35%" }}>{nama}</td>
              <td rowSpan="3" style={{ width: "50%", verticalAlign: "middle", textAlign: "center", backgroundColor: "#8faadc", fontWeight: "bold", fontSize: "16px", textTransform: "uppercase" }}>
                {type === "kpi" ? "KPI KARYAWAN RSIA ANNISA" : "TABUNGAN KARYAWAN RSIA ANNISA"}
                <br />
                <span style={{ display: "inline-block", marginTop: "5px" }}>BULAN {bulanIni}</span>
              </td>
            </tr>
            <tr>
              <td style={{ fontWeight: "bold" }}>JABATAN</td>
              <td>{jabatan}</td>
            </tr>
            <tr>
              <td style={{ fontWeight: "bold" }}>DEPARTEMENT</td>
              <td>{departement}</td>
            </tr>
          </tbody>
        </table>

        <table className="main-table">
          <thead>
            <tr>
              {isEditMode && <th style={{ width: "3%" }} className="no-print">Aksi</th>}
              <th style={{ width: "3%" }}>NO</th>

              {type === "kpi" ? (
                <>
                  <th style={{ width: "15%" }}>INDIKATOR KINERJA</th>
                  <th style={{ width: "18%" }}>PARAMETER</th>
                  <th style={{ width: "11%" }}>SUMBER</th>
                  <th style={{ width: "20%" }}>KPI</th>
                  <th style={{ width: "7%" }}>TARGET</th>
                  <th style={{ width: "7%" }}>AKTUAL</th>
                  <th style={{ width: "5%" }}>NILAI</th>
                  <th style={{ width: "5%" }}>BOBOT</th>
                  <th style={{ width: "9%" }}>NILAI AKHIR KPI</th>
                </>
              ) : (
                <>
                  <th style={{ width: "16%" }}>KERJA TABUNGAN GAJI</th>
                  <th style={{ width: "14%" }}>HASIL BERWUJUD</th>
                  <th style={{ width: "14%" }}>BUKTI NILAI</th>
                  <th style={{ width: "8%" }}>KONVERSI UANG</th>
                  <th style={{ width: "15%" }}>PARAMETER</th>
                  <th style={{ width: "6%" }}>TARGET</th>
                  <th style={{ width: "6%" }}>AKTUAL</th>
                  <th style={{ width: "5%" }}>NILAI</th>
                  <th style={{ width: "5%" }}>BOBOT</th>
                  <th style={{ width: "8%" }}>NILAI TABUNGAN</th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {type === "kpi"
              ? isEditMode
                ? localItems.map((item, index) => {
                    const isUnfilled = item.actual === "" || item.actual === undefined;
                    const targetVal = parseIdnNumber(item.target);
                    const actualVal = parseIdnNumber(item.actual);
                    let calculatedNilai = 0;
                    if (targetVal === 0 && actualVal === 0) calculatedNilai = 100;
                    else if (targetVal !== 0) calculatedNilai = (actualVal / targetVal) * 100;
                    
                    const bobotRaw = String(item.bobot || "0").trim();
                    let bobot = parseFloat(bobotRaw.replace("%", "").replace(",", ".")) || 0;
                    const nilaiAkhir = (calculatedNilai * bobot) / 100;

                    return (
                      <tr key={item.id}>
                        <td className="no-print" style={{ textAlign: "center", verticalAlign: "middle" }}><button className="btn-del" title="Hapus Baris" onClick={() => handleDeleteRow(item.id)}>🗑️</button></td>
                        <td style={{ textAlign: "center", verticalAlign: "middle" }}>{index + 1}</td>
                        <td><textarea className="edit-input" value={item.area_kinerja} onChange={(e) => handleEditCell(item.id, "area_kinerja", e.target.value)} rows={5} /></td>
                        <td><textarea className="edit-input" value={item.parameter} onChange={(e) => handleEditCell(item.id, "parameter", e.target.value)} rows={5} /></td>
                        <td><textarea className="edit-input" value={item.sumber} onChange={(e) => handleEditCell(item.id, "sumber", e.target.value)} rows={5} /></td>
                        <td><textarea className="edit-input" value={item.indikator_kpi || ""} onChange={(e) => handleEditCell(item.id, "indikator_kpi", e.target.value)} rows={5} /></td>
                        <td style={{ verticalAlign: "middle" }}>
                           <input 
                             className="edit-input" 
                             style={{ textAlign: "center", backgroundColor: (isNaN(parseFloat(String(item.target).replace(",", "."))) || /[^0-9.,%\s]/.test(String(item.target || ""))) ? "#fecaca" : "#fdfde8" }} 
                             value={item.target} 
                             onChange={(e) => handleEditCell(item.id, "target", e.target.value)} 
                           />
                        </td>
                        <td style={{ verticalAlign: "middle" }}><input className="edit-input input-locked" readOnly style={{ textAlign: "center" }} value={item.actual || ""} /></td>
                        <td style={{ verticalAlign: "middle" }}><input className="edit-input input-locked" readOnly style={{ textAlign: "center", fontWeight: "bold" }} value={Math.round(calculatedNilai)} /></td>
                        <td style={{ verticalAlign: "middle" }}><input className="edit-input" style={{ textAlign: "center" }} value={item.bobot} onChange={(e) => handleEditCell(item.id, "bobot", e.target.value)} /></td>
                        <td style={{ textAlign: "center", verticalAlign: "middle", backgroundColor: "#f3f4f6", fontWeight: "bold" }}>{isUnfilled ? "-" : Math.round(nilaiAkhir)}</td>
                      </tr>
                    );
                  })
                : Object.keys(groupedKpi).map((area, areaIndex) => {
                    const rowCount = groupedKpi[area].length;
                    return groupedKpi[area].map((item, itemIndex) => {
                      const isUnfilled = item.actual === "" || item.actual === undefined;
                      const targetVal = parseIdnNumber(item.target);
                      const actualVal = parseIdnNumber(item.actual);
                      let calculatedNilai = 0;
                      if (targetVal === 0 && actualVal === 0) calculatedNilai = 100;
                      else if (targetVal !== 0) calculatedNilai = (actualVal / targetVal) * 100;
                      
                      const bobotRaw = String(item.bobot || "0").trim();
                      let bobot = parseFloat(bobotRaw.replace("%", "").replace(",", ".")) || 0;
                      const nilaiAkhir = (calculatedNilai * bobot) / 100;
                      
                      totalBobot += bobot;
                      if (!isUnfilled) totalNilaiAkhir += nilaiAkhir; 

                      return (
                        <tr key={item.id}>
                          {itemIndex === 0 && (
                            <>
                              <td rowSpan={rowCount} style={{ textAlign: "center", verticalAlign: "middle" }}>{areaIndex + 1}</td>
                              <td rowSpan={rowCount} style={{ whiteSpace: "pre-wrap", textAlign: "left", verticalAlign: "middle", padding: "10px 8px" }}><b>{area}</b></td>
                            </>
                          )}
                          <td style={{ whiteSpace: "pre-wrap", textAlign: "left", verticalAlign: "middle", padding: "10px 8px" }}>{item.parameter || "-"}</td>
                          <td style={{ whiteSpace: "pre-wrap", textAlign: "left", verticalAlign: "middle", padding: "10px 8px" }}>{item.sumber || "-"}</td>
                          <td style={{ whiteSpace: "pre-wrap", textAlign: "left", verticalAlign: "middle", padding: "10px 8px" }}>{formatTampilanBersih(item.indikator_kpi)}</td>
                          <td style={{ whiteSpace: "pre-wrap", textAlign: "center", verticalAlign: "middle", padding: "10px 8px" }}>{item.target || "-"}</td>
                          <td style={{ textAlign: "center", verticalAlign: "middle" }}>{isUnfilled ? "-" : item.actual}</td>
                          <td style={{ textAlign: "center", verticalAlign: "middle" }}>{isUnfilled ? "-" : Math.round(calculatedNilai)}</td>
                          <td style={{ textAlign: "center", verticalAlign: "middle" }}>{bobot}%</td>
                          <td style={{ textAlign: "center", verticalAlign: "middle", fontWeight:"bold" }}>
                             {isUnfilled ? "-" : Math.round(nilaiAkhir)}
                          </td>
                        </tr>
                      );
                    });
                  })
              : 
                localItems.map((item, index) => {
                  const isUnfilled = item.actual === "" || item.actual === undefined;
                  const targetVal = parseIdnNumber(item.target);
                  const actualVal = parseIdnNumber(item.actual);
                  let calculatedNilai = 0;
                  if (targetVal === 0 && actualVal === 0) calculatedNilai = 100;
                  else if (targetVal !== 0) calculatedNilai = (actualVal / targetVal) * 100;
                  
                  const bobotRaw = String(item.bobot || "0").trim();
                  let bobot = parseFloat(bobotRaw.replace("%", "").replace(",", ".")) || 0;
                  const nilaiAkhir = (calculatedNilai * bobot) / 100;
                  
                  totalBobot += bobot;
                  if (!isUnfilled) totalNilaiAkhir += nilaiAkhir;

                  return (
                    <tr key={item.id}>
                      {isEditMode && (<td className="no-print" style={{ textAlign: "center", verticalAlign: "middle" }}><button className="btn-del" onClick={() => handleDeleteRow(item.id)}>🗑️</button></td>)}
                      <td style={{ textAlign: "center", verticalAlign: "middle" }}>{index + 1}</td>

                      {isEditMode ? (
                        <>
                          <td><textarea className="edit-input" value={item.kerja_tabungan_gaji || ""} onChange={(e) => handleEditCell(item.id, "kerja_tabungan_gaji", e.target.value)} rows={5} /></td>
                          <td><textarea className="edit-input" value={item.hasil_berwujud} onChange={(e) => handleEditCell(item.id, "hasil_berwujud", e.target.value)} rows={5} /></td>
                          <td><textarea className="edit-input" value={item.bukti_nilai} onChange={(e) => handleEditCell(item.id, "bukti_nilai", e.target.value)} rows={5} /></td>
                          <td><textarea className="edit-input" value={item.konversi_uang} onChange={(e) => handleEditCell(item.id, "konversi_uang", e.target.value)} rows={5} /></td>
                          <td><textarea className="edit-input" value={item.parameter} onChange={(e) => handleEditCell(item.id, "parameter", e.target.value)} rows={5} /></td>
                        </>
                      ) : (
                        <>
                          <td style={{ whiteSpace: "pre-wrap", textAlign: "left", verticalAlign: "middle", padding: "10px 8px" }}>{formatTampilanBersih(item.kerja_tabungan_gaji)}</td>
                          <td style={{ whiteSpace: "pre-wrap", textAlign: "left", verticalAlign: "middle", padding: "10px 8px" }}>{item.hasil_berwujud || "-"}</td>
                          <td style={{ whiteSpace: "pre-wrap", textAlign: "left", verticalAlign: "middle", padding: "10px 8px" }}>{item.bukti_nilai || "-"}</td>
                          <td style={{ whiteSpace: "pre-wrap", textAlign: "left", verticalAlign: "middle", padding: "10px 8px" }}>{item.konversi_uang || "-"}</td>
                          <td style={{ whiteSpace: "pre-wrap", textAlign: "left", verticalAlign: "middle", padding: "10px 8px" }}>{item.parameter || "-"}</td>
                        </>
                      )}

                      <td style={{ textAlign: "center", verticalAlign: "middle" }}>
                        {isEditMode ? (
                           <input 
                             className="edit-input" 
                             style={{ textAlign: "center", backgroundColor: (isNaN(parseFloat(String(item.target).replace(",", "."))) || /[^0-9.,%\s]/.test(String(item.target || ""))) ? "#fecaca" : "#fdfde8" }} 
                             value={item.target} 
                             onChange={(e) => handleEditCell(item.id, "target", e.target.value)} 
                           />
                        ) : (
                          item.target || "-"
                        )}
                      </td>
                      <td style={{ textAlign: "center", verticalAlign: "middle" }}>{isEditMode ? <input className="edit-input input-locked" readOnly style={{ textAlign: "center" }} value={item.actual || ""} /> : (isUnfilled ? "-" : item.actual)}</td>
                      <td style={{ textAlign: "center", verticalAlign: "middle" }}>{isEditMode ? <input className="edit-input input-locked" readOnly style={{ textAlign: "center", fontWeight: "bold" }} value={Math.round(calculatedNilai)} /> : (isUnfilled ? "-" : Math.round(calculatedNilai))}</td>
                      <td style={{ textAlign: "center", verticalAlign: "middle" }}>{isEditMode ? <input className="edit-input" style={{ textAlign: "center" }} value={item.bobot} onChange={(e) => handleEditCell(item.id, "bobot", e.target.value)} /> : `${bobot}%`}</td>
                      <td style={{ textAlign: "center", verticalAlign: "middle", backgroundColor: isEditMode ? "#f3f4f6" : "transparent", fontWeight: "bold" }}>{isUnfilled ? "-" : Math.round(nilaiAkhir)}</td>
                    </tr>
                  );
                })}

            {!isEditMode && (
              <tr style={{ fontWeight: "bold", backgroundColor: "#8faadc" }}>
                <td colSpan={type === "kpi" ? 8 : 9} style={{ textAlign: "right", paddingRight: "15px" }}>TOTAL</td>
                <td style={{ textAlign: "center" }}>{Math.round(totalBobot)}%</td>
                <td style={{ textAlign: "center", fontSize:"13px" }}>{Math.round(totalNilaiAkhir)}</td>
              </tr>
            )}
          </tbody>
        </table>

        <div className="signature-section">
          
          <div className={`sign-box ${(!atasan2 || atasan2.trim() === "") ? "hide-on-print" : ""}`}>
            <p style={{ margin: 0, fontSize: "11px" }}>Mengetahui,</p>
            <p style={{ margin: 0, fontSize: "11px" }}>Atasan 2 Tingkat</p>
            <div className="sign-space">
              {ttdAtasan2 ? (
                <img src={formatDriveUrl(ttdAtasan2)} alt="" style={{ maxHeight: "85px", maxWidth: "180px", objectFit: "contain" }} referrerPolicy="no-referrer" onError={(e) => { e.target.style.display = 'none'; }} />
              ) : isCurrentUserAtasan2 ? (
                <button 
                  className="btn btn-approve no-print" 
                  style={{ fontSize: "11px", padding: "8px 16px", marginTop: "10px", borderRadius: "6px" }} 
                  onClick={() => { setActiveSignLevel(2); setShowSignModal(true); }}
                >
                  📝 Tanda Tangan
                </button>
              ) : (
                <span style={{ color: "#9ca3af", fontStyle: "italic", fontSize: "10px" }}>Menunggu Tanda Tangan</span>
              )}
            </div>
            <div className="sign-line">
              {(reportStatus === "DRAFT" || !reportStatus) && !isEditMode ? (
                <div className="no-print">
                  {isLockedAtasan2 || ttdAtasan2 || reportStatus !== "DRAFT" ? (
                    <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "6px", padding: "4px", background: "#f1f5f9", borderRadius: "4px", border: "1px solid #cbd5e1" }}>
                      <span style={{ fontSize: "11px", color: "#334155", fontWeight: "bold" }}>{atasan2}</span>
                      {!ttdAtasan2 && reportStatus === "DRAFT" && (
                        <button 
                          onClick={() => { setIsLockedAtasan2(false); setAtasan2(""); }} 
                          style={{ background: "transparent", border: "none", cursor: "pointer", padding: 0, fontSize: "10px" }} 
                          title="Ganti Atasan"
                        >❌</button>
                      )}
                    </div>
                  ) : (
                    <>
                      <input
                        type="text"
                        value={atasan2}
                        onChange={(e) => setAtasan2(e.target.value)}
                        onFocus={() => setSuggest2(true)}
                        onBlur={() => setTimeout(() => setSuggest2(false), 200)}
                        placeholder="Ketik & Cari Atasan..."
                        style={{ width: "100%", textAlign: "center", border: "1px dashed #94a3b8", borderRadius: "4px", padding: "6px", fontSize: "11px", background: "#f8fafc", boxSizing: "border-box" }}
                      />
                      {suggest2 && atasan2 && (
                        <ul style={{ position: "absolute", bottom: "100%", left: 0, right: 0, background: "#fff", border: "1px solid #cbd5e1", borderRadius: "4px", listStyle: "none", padding: 0, margin: "0 0 4px 0", maxHeight: "120px", overflowY: "auto", zIndex: 10, textAlign: "left", boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1)" }}>
                          {masterAtasanList.filter(n => n.toLowerCase().includes(atasan2.toLowerCase())).map(n => (
                            <li 
                              key={n} 
                              onMouseDown={() => { setAtasan2(n); setIsLockedAtasan2(true); setSuggest2(false); }} 
                              style={{ padding: "6px 8px", fontSize: "11px", cursor: "pointer", borderBottom: "1px solid #f1f5f9" }}
                            >{n}</li>
                          ))}
                        </ul>
                      )}
                    </>
                  )}
                </div>
              ) : (
                <span className="no-print" style={{ fontWeight: "bold", fontSize: "12px" }}>{atasan2}</span>
              )}
              <span className="only-print" style={{ fontWeight: "bold", fontSize: "12px" }}>{atasan2}</span>
            </div>
          </div>

          <div className="sign-box">
            <p style={{ margin: 0, fontSize: "11px" }}>Mengetahui,</p>
            <p style={{ margin: 0, fontSize: "11px" }}>Atasan 1 Tingkat</p>
            <div className="sign-space">
              {ttdAtasan1 ? (
                <img src={formatDriveUrl(ttdAtasan1)} alt="" style={{ maxHeight: "85px", maxWidth: "180px", objectFit: "contain" }} referrerPolicy="no-referrer" onError={(e) => { e.target.style.display = 'none'; }} />
              ) : isCurrentUserAtasan1 ? (
                <button 
                  className="btn btn-approve no-print" 
                  style={{ fontSize: "11px", padding: "8px 16px", marginTop: "10px", borderRadius: "6px" }} 
                  onClick={() => { setActiveSignLevel(1); setShowSignModal(true); }}
                >
                  📝 Tanda Tangan
                </button>
              ) : (
                <span style={{ color: "#9ca3af", fontStyle: "italic", fontSize: "10px" }}>Menunggu Tanda Tangan</span>
              )}
            </div>
            <div className="sign-line">
              {(reportStatus === "DRAFT" || !reportStatus) && !isEditMode ? (
                <div className="no-print">
                  {isLockedAtasan1 || ttdAtasan1 || reportStatus !== "DRAFT" ? (
                    <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "6px", padding: "4px", background: "#f1f5f9", borderRadius: "4px", border: "1px solid #cbd5e1" }}>
                      <span style={{ fontSize: "11px", color: "#334155", fontWeight: "bold" }}>{atasan1}</span>
                      {!ttdAtasan1 && reportStatus === "DRAFT" && (
                        <button 
                          onClick={() => { setIsLockedAtasan1(false); setAtasan1(""); }} 
                          style={{ background: "transparent", border: "none", cursor: "pointer", padding: 0, fontSize: "10px" }} 
                          title="Ganti Atasan"
                        >❌</button>
                      )}
                    </div>
                  ) : (
                    <>
                      <input
                        type="text"
                        value={atasan1}
                        onChange={(e) => setAtasan1(e.target.value)}
                        onFocus={() => setSuggest1(true)}
                        onBlur={() => setTimeout(() => setSuggest1(false), 200)}
                        placeholder="Ketik & Cari Atasan..."
                        style={{ width: "100%", textAlign: "center", border: "1px dashed #94a3b8", borderRadius: "4px", padding: "6px", fontSize: "11px", background: "#f8fafc", boxSizing: "border-box" }}
                      />
                      {suggest1 && atasan1 && (
                        <ul style={{ position: "absolute", bottom: "100%", left: 0, right: 0, background: "#fff", border: "1px solid #cbd5e1", borderRadius: "4px", listStyle: "none", padding: 0, margin: "0 0 4px 0", maxHeight: "120px", overflowY: "auto", zIndex: 10, textAlign: "left", boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1)" }}>
                          {masterAtasanList.filter(n => n.toLowerCase().includes(atasan1.toLowerCase())).map(n => (
                            <li 
                              key={n} 
                              onMouseDown={() => { setAtasan1(n); setIsLockedAtasan1(true); setSuggest1(false); }} 
                              style={{ padding: "6px 8px", fontSize: "11px", cursor: "pointer", borderBottom: "1px solid #f1f5f9" }}
                            >{n}</li>
                          ))}
                        </ul>
                      )}
                    </>
                  )}
                </div>
              ) : (
                <span className="no-print" style={{ fontWeight: "bold", fontSize: "12px" }}>{atasan1}</span>
              )}
              <span className="only-print" style={{ fontWeight: "bold", fontSize: "12px" }}>{atasan1}</span>
            </div>
          </div>
          
          <div className="sign-box">
            <p style={{ margin: 0, fontSize: "11px" }}>
              <span className="only-print">Pekanbaru, {tanggal}</span>
              <span className="no-print">
                Pekanbaru, <input type="text" value={tanggal} onChange={(e)=>setTanggal(e.target.value)} style={{ border:"none", width:"80px", textAlign:"center", fontSize:"11px" }} />
              </span>
            </p>
            <p style={{ margin: 0, fontSize: "11px" }}>Karyawan,</p>
            <div className="sign-space">
              {userSignature ? (
                <img src={userSignature} alt="" style={{ maxHeight: "85px", maxWidth: "180px", objectFit: "contain" }} referrerPolicy="no-referrer" onError={(e) => { e.target.style.display = 'none'; }} />
              ) : (
                <span style={{ color: "#9ca3af", fontStyle: "italic", fontSize: "10px" }}>Belum ada TTD Tersimpan</span>
              )}
            </div>
            <div className="sign-line">
              <span style={{ fontWeight: "bold", fontSize: "12px" }}>{nama}</span>
            </div>
          </div>

        </div>
      </div>
      
      <Modal 
        isOpen={modalOpen} 
        title={modalTitle} 
        message={modalMessage} 
        type={modalType} 
        onClose={() => setModalOpen(false)} 
        onConfirm={() => {
          setModalOpen(false);
          if (confirmPrintAction) confirmPrintAction();
        }}
        confirmLabel="Oke" 
        confirmOnly={modalConfirmOnly}
      />
      <Modal isOpen={loadingModalOpen} title="Memproses" message="Mohon tunggu..." type="loading" isLoading={true} onClose={null} />
      
      {showSignModal && (
        <div className="ekp-overlay no-print" onClick={() => setShowSignModal(false)} style={{ position: "fixed", inset: 0, background: "rgba(15, 23, 42, 0.4)", backdropFilter: "blur(8px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999 }}>
          <div className="ekp-modal" onClick={(e) => e.stopPropagation()} style={{ width: '90%', maxWidth: '400px', textAlign: 'center', padding: '24px', borderRadius: '24px', background: "#fff", boxShadow: "0 25px 50px -12px rgba(0,0,0,0.15)" }}>
            <h3 style={{ marginTop: 0, color: '#1f2937', fontSize: "1.25rem", fontWeight: 800 }}>Tanda Tangan Atasan</h3>
            <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '20px' }}>Gunakan jari atau kursor untuk menandatangani kotak di bawah ini.</p>

            <div style={{ border: '2px dashed #94a3b8', borderRadius: '12px', background: '#f8fafc', marginBottom: '24px', display: 'inline-block', overflow: 'hidden' }}>
              <canvas
                width={300}
                height={150}
                className="popup-signature-canvas"
                style={{ touchAction: 'none', cursor: 'crosshair', display: 'block' }}
                onMouseDown={(e)=>{const ctx=e.target.getContext("2d");ctx.lineWidth=2;ctx.beginPath();ctx.moveTo(e.nativeEvent.offsetX, e.nativeEvent.offsetY);e.target.onmousemove=(ev)=>{ctx.lineTo(ev.offsetX, ev.offsetY);ctx.stroke();}}}
                onMouseUp={(e)=>{e.target.onmousemove=null;}}
                onMouseLeave={(e)=>{e.target.onmousemove=null;}}
                onTouchStart={(e)=>{const ctx=e.target.getContext("2d");ctx.lineWidth=2;ctx.beginPath();const rect=e.target.getBoundingClientRect();ctx.moveTo(e.touches[0].clientX-rect.left,e.touches[0].clientY-rect.top);e.target.ontouchmove=(ev)=>{ev.preventDefault();ctx.lineTo(ev.touches[0].clientX-rect.left,ev.touches[0].clientY-rect.top);ctx.stroke();}}}
                onTouchEnd={(e)=>{e.target.ontouchmove=null;}}
              />
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px' }}>
              <button
                 style={{ flex: 1, padding: '12px', background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: '12px', cursor: 'pointer', fontWeight: 'bold', transition: "0.2s" }}
                 onClick={() => {
                   const cvs = document.querySelector(".popup-signature-canvas");
                   if (cvs) {
                     const ctx = cvs.getContext("2d");
                     ctx.clearRect(0, 0, cvs.width, cvs.height);
                     ctx.beginPath();
                   }
                 }}
              >
                Hapus & Ulang
              </button>
              <button
                style={{ flex: 1, padding: '12px', background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', color: 'white', border: 'none', borderRadius: '12px', cursor: 'pointer', fontWeight: 'bold', transition: "0.2s", boxShadow: "0 4px 10px rgba(16, 185, 129, 0.3)" }}
                onClick={submitSignature}
              >
                Selesai
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PrintReport;